import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { parseJsonResponse } from '../services/api';

export type UserRole = 'owner' | 'cpa';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  userRole: UserRole | null;
  isCPA: boolean;
  isOwner: boolean;
  login: (password: string) => Promise<boolean>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || 'https://tax-expense-tracker-api.mr-doug-sullivan.workers.dev';
if (API_BASE_URL.includes('pages.dev')) {
  throw new Error('API_BASE_URL must point to Worker. Set VITE_API_BASE_URL=https://tax-expense-tracker-api.mr-doug-sullivan.workers.dev');
}
if (import.meta.env.DEV) console.log('API_BASE_URL', API_BASE_URL);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userRole, setUserRole] = useState<UserRole | null>(null);

  // Check for existing session on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('auth_token');
      const savedRole = localStorage.getItem('user_role') as UserRole | null;
      
      if (token) {
        try {
          const response = await fetch(`${API_BASE_URL}/api/auth/verify`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (response.ok) {
            const data = await parseJsonResponse<{ role?: string }>(response) ?? {};
            setIsAuthenticated(true);
            setUserRole(data.role || savedRole || 'owner');
          } else {
            localStorage.removeItem('auth_token');
            localStorage.removeItem('user_role');
            setIsAuthenticated(false);
            setUserRole(null);
          }
        } catch (error) {
          console.error('Auth check failed:', error);
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user_role');
          setIsAuthenticated(false);
          setUserRole(null);
        }
      }
      setIsLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (password: string): Promise<boolean> => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ password })
      });

      if (response.ok) {
        const data = await parseJsonResponse<{ token?: string; role?: string }>(response) ?? {};
        const token = data.token ?? '';
        const role = data.role || 'owner';
        // Force clear cached empty state from previous session (transparency fix)
        window.localStorage.clear();
        localStorage.setItem('auth_token', token);
        localStorage.setItem('user_role', role);
        setIsAuthenticated(true);
        setUserRole(role);
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.error('Login failed:', error);
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_role');
    setIsAuthenticated(false);
    setUserRole(null);
  };

  const isCPA = userRole === 'cpa';
  const isOwner = userRole === 'owner';

  return (
    <AuthContext.Provider value={{ 
      isAuthenticated, 
      isLoading, 
      userRole, 
      isCPA, 
      isOwner, 
      login, 
      logout 
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}


















